import { createAction } from '@reduxjs/toolkit'

import {
  AgentSourcedRunScope,
  IAgentSourcedCreator,
  IAgentSourcedCreatorMeta,
} from '@components/CreatorSearch/types'
import { IHateoasLink } from '@store'
import {
  AgentSourcedCreatorListing,
  IAgentSourcedCreatorActionPayload,
  IAgentSourcedCreatorActionSuccessPayload,
  IBulkRejectAgentSourcedCreatorsPayload,
  IFetchAgentSourcedCreatorsCountPayload,
  IFetchAgentSourcedCreatorsCountSuccessPayload,
  IFetchAgentSourcedCreatorsPayload,
} from './types'

export const fetchAgentSourcedCreators = createAction<IFetchAgentSourcedCreatorsPayload>(
  'agentSourcedCreators/fetchCreators',
)
export const fetchAgentSourcedCreatorsSuccess = createAction<{
  creators: IAgentSourcedCreator[]
  collectionLinks: IHateoasLink[]
  metadata: IAgentSourcedCreatorMeta | null
  append?: boolean
  // Records which listing this success belongs to so per-tab count selectors
  // can return values only when their listing is currently loaded.
  listing?: AgentSourcedCreatorListing
  // Which campaign this response is FOR (BRAND-9550 review fix). The reducer's
  // one-shot "land on the run I just started" selection is armed per campaign
  // and must only be consumed by that campaign's own response — without this
  // the payload named no campaign at all, so any campaign's response could
  // claim it. Optional because the token-mode listing (fetchCreatorsByToken)
  // has no campaign id; that path can't start runs, and an absent id never
  // matches, so it simply never consumes a pending selection.
  campaignId?: number
}>('agentSourcedCreators/fetchCreatorsSuccess')
export const fetchAgentSourcedCreatorsFailure = createAction<string>(
  'agentSourcedCreators/fetchCreatorsFailure',
)

export const fetchAgentSourcedCreatorsByToken = createAction<{ token: string }>(
  'agentSourcedCreators/fetchCreatorsByToken',
)

export const fetchAgentSourcedCreatorsCount = createAction<IFetchAgentSourcedCreatorsCountPayload>(
  'agentSourcedCreators/fetchCreatorsCount',
)
export const fetchAgentSourcedCreatorsCountSuccess =
  createAction<IFetchAgentSourcedCreatorsCountSuccessPayload>(
    'agentSourcedCreators/fetchCreatorsCountSuccess',
  )
export const fetchAgentSourcedCreatorsCountFailure = createAction<string>(
  'agentSourcedCreators/fetchCreatorsCountFailure',
)

export const approveAgentSourcedCreator = createAction<IAgentSourcedCreatorActionPayload>(
  'agentSourcedCreators/approveCreator',
)
export const rejectAgentSourcedCreator = createAction<IAgentSourcedCreatorActionPayload>(
  'agentSourcedCreators/rejectCreator',
)
export const undoAgentSourcedCreatorAction = createAction<IAgentSourcedCreatorActionPayload>(
  'agentSourcedCreators/undoCreatorAction',
)
export const shortlistAgentSourcedCreator = createAction<IAgentSourcedCreatorActionPayload>(
  'agentSourcedCreators/shortlistCreator',
)
export const unshortlistAgentSourcedCreator = createAction<IAgentSourcedCreatorActionPayload>(
  'agentSourcedCreators/unshortlistCreator',
)
export const agentSourcedCreatorActionSuccess =
  createAction<IAgentSourcedCreatorActionSuccessPayload>(
    'agentSourcedCreators/creatorActionSuccess',
  )
export const agentSourcedCreatorActionFailure = createAction<{
  creatorId: number
  error: string
}>('agentSourcedCreators/creatorActionFailure')

// BRAND-9514 bulk reject: PUTs the collection-level bulk_reject link with
// {ids, comment?}. All-or-nothing on the BE — success refreshes through the
// existing fetch flows (see sagas/agentSourcedCreators/bulkRejectCreators.ts);
// failure carries the BE's verbatim message.
export const bulkRejectAgentSourcedCreators = createAction<IBulkRejectAgentSourcedCreatorsPayload>(
  'agentSourcedCreators/bulkRejectCreators',
)
export const bulkRejectAgentSourcedCreatorsSuccess = createAction(
  'agentSourcedCreators/bulkRejectCreatorsSuccess',
)
export const bulkRejectAgentSourcedCreatorsFailure = createAction<string>(
  'agentSourcedCreators/bulkRejectCreatorsFailure',
)
// Scrubs the terminal succeeded/failed status back to 'idle'. Dispatched by
// the view AFTER it consumes the pending -> terminal transition (post-render,
// from the transition effect) — a fetch-time reducer reset would instead race
// React 19's automatic batching (the saga puts success + refetch in one task,
// collapsing to a single render in which the transition is never observed).
export const bulkRejectAgentSourcedCreatorsStatusReset = createAction(
  'agentSourcedCreators/bulkRejectCreatorsStatusReset',
)

// BRAND-9306 run-aware review views: selects a specific run ordinal, 'all'
// runs, or null (latest, the default) for the vibe-check/interested/
// shortlisted listings. Read by fetchCreatorsSaga to build `?sourcing_run=`.
export const setSelectedRunScope = createAction<AgentSourcedRunScope>(
  'agentSourcedCreators/setSelectedRunScope',
)

export const downloadShortlistCsv = createAction('agentSourcedCreators/downloadShortlistCsv')
export const downloadShortlistCsvFailure = createAction<string>(
  'agentSourcedCreators/downloadShortlistCsvFailure',
)
